import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import z from 'zod';
import { publicHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { couponsApiPath } from './apiPath';

const SortSchema = z.enum(['POPULAR', 'LOW_PRICE', 'HIGH_PRICE', 'SALE_THREE_MONTH_COUNT']);

const GetCouponGoodsRequestSchema = z
  .object({
    couponNo: z.number().int(),
    categoryCode: z.string().optional(),
    sort: SortSchema.optional(),
    page: z.number().int().positive().optional(),
    size: z.number().int().positive().max(100).optional(),
  })
  .describe('GetCouponGoodsRequest');

export type GetCouponGoodsRequest = z.infer<typeof GetCouponGoodsRequestSchema>;

export const CouponGoodsItemSchema = z
  .object({
    goodsNo: z.number().int(),
    imageUrl: z.string(),
    brandName: z.string(),
    goodsName: z.string(),
    normalPrice: z.number().int(),
    price: z.number().int(),
    salesRate: z.number().int(),
    couponPrice: z.number().int().nullable().optional(),
    couponSalesRate: z.number().int().nullable().optional(),
    likeCount: z.number().int(),
    isLiked: z.boolean(),
    reviewScore: z.string(),
    reviewCount: z.number().int(),
    existsCoupon: z.boolean().nullish().describe('쿠폰 존재 여부'),
    isSoldOut: z.boolean(),
    hasOptionPrice: z.boolean().nullable().optional(),
    linkUrl: z.string(),
    totalQuantity: z.number().int().nullable().optional(),
    extraDiscountAmount: z
      .number()
      .int()
      .nullish()
      .describe('추가할인 금액(원). 표시는 finalPrice 사용 (주문 도메인 전용, MUSINSA 전용)'),
    finalPrice: z
      .number()
      .int()
      .nullish()
      .describe('최종가 = 세일가 + 상품쿠폰 + 추가할인 (장바구니쿠폰 제외, MUSINSA 전용)'),
    finalDiscount: z
      .number()
      .int()
      .nullish()
      .describe('정상가 대비 finalPrice 할인율 (MUSINSA 전용)'),
    isLowestPrice: z
      .boolean()
      .nullish()
      .describe('RADAR 추가할인 최저가 보장 여부 — true 시 최저가도전 라벨 (MUSINSA 전용)'),
  })
  .describe('CouponGoodsItem');

export type CouponGoodsItem = z.infer<typeof CouponGoodsItemSchema>;

export const CouponGoodsHeaderSchema = z
  .object({
    isOwner: z.boolean(),
    couponNo: z.number().int(),
    brandId: z.string(),
    couponName: z.string(),
    salesRate: z.number().int(),
    useStartDate: z.string(),
    useEndDate: z.string(),
    status: z.enum(['AVAILABLE', 'RECEIVED', 'EXHAUSTED', 'EXPIRED', 'STOPPED']),
    list: z.array(CouponGoodsItemSchema),
  })
  .describe('CouponGoodsHeader');

export const GetCouponGoodsResponseSchema = createApiResponseSchema(CouponGoodsHeaderSchema);
export type GetCouponGoodsResponse = z.infer<typeof GetCouponGoodsResponseSchema>;

export const fetchCouponGoods = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetCouponGoodsRequestSchema),
    async ({
      couponNo,
      categoryCode,
      sort,
      page,
      size,
    }: GetCouponGoodsRequest): Promise<GetCouponGoodsResponse> =>
      // NOTE: executeStandardRequest 로 감싸면 AxiosError → StandardResponseErrorModel
      // (`{data, status, message}` 평탄화) 로 정규화되어 features 레이어에서 isApiError 만으로 분기 가능.
      executeStandardRequest(() =>
        publicHttpClient.get<GetCouponGoodsResponse>(couponsApiPath.getCouponGoods(couponNo), {
          categoryCode,
          sort,
          page,
          size,
        }),
      ),
    validateApiResponse(GetCouponGoodsResponseSchema),
  ),
);
